import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Self,
    viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { noop, Subject, takeUntil } from 'rxjs';
import { FromToLabel } from '../../search-page/search-param.model';
import { SE_UI_DATE_FORMATS } from '../se-ui-date-formats';

function capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

let nextUniqueId = 0;
interface IDataRange {
    from: moment.Moment | null;
    to: moment.Moment | null;
}

interface IDataRangeInput {
    from: moment.Moment | string | null;
    to: moment.Moment | string | null;
}
export type DurationKey = 'today' | 'yesterday' | 'years' | 'months' | 'weeks' | 'days';

export interface IDataRangeDuration {
    label?: string;
    key: DurationKey;
    interval: number;
}

@Component({
    selector: 'se-date-range',
    templateUrl: './se-date-range.component.html',
    styleUrls: ['./se-date-range.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: SeDateRangeComponent },
        { provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_FORMATS },
    ],
    imports: [
        MatButtonModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatIconModule,
        MatMenuModule,
        ReactiveFormsModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class SeDateRangeComponent
    implements ControlValueAccessor, MatFormFieldControl<IDataRange>, OnInit, OnChanges, OnDestroy
{
    maxSelectableDate = moment();
    wrapperElement = viewChild<ElementRef<HTMLElement>>('wrapper');

    _uid = `mat-input-${nextUniqueId++}`;
    form = new FormGroup({
        from: new FormControl<moment.Moment | null>(null),
        to: new FormControl<moment.Moment | null>(null),
    });
    stateChanges = new Subject<void>();
    focused = false;
    touched = false;

    onChange: (value: IDataRange | null) => void = noop;

    onTouched: () => void = noop;

    @Output() dataRangeChange: EventEmitter<IDataRange | null> = new EventEmitter();
    @Output() searchRequired: EventEmitter<any> = new EventEmitter<any>();

    @Input()
    get value(): IDataRange | null {
        if (this.form.valid) {
            const {
                value: { from, to },
            } = this.form;
            if (!from && !to) return null;
            return {
                from: from?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc(true) ?? null,
                to:
                    to?.set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).utc(true) ??
                    moment().set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).utc(true),
            };
        }
        return null;
    }
    set value(_valueInput: IDataRange | null) {
        const valueInput = _valueInput ?? null;
        if (!valueInput) {
            this.form.controls.from.setValue(null);
            this.form.controls.to.setValue(null);
        } else {
            const { from, to } = valueInput;
            this.form.controls.from.setValue(from);
            this.form.controls.to.setValue(to);
        }
        this.stateChanges.next();
    }

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder: string = '';

    @Input()
    get placeholderStartDate(): string {
        return this._placeholderStartDate;
    }

    set placeholderStartDate(value: string) {
        this._placeholderStartDate = value;
        this.stateChanges.next();
    }
    private _placeholderStartDate: string = '';

    @Input()
    get placeholderEndDate(): string {
        return this._placeholderEndDate;
    }

    set placeholderEndDate(value: string) {
        this._placeholderEndDate = value;
        this.stateChanges.next();
    }
    private _placeholderEndDate: string = '';

    @Input()
    get label(): FromToLabel {
        return this._label;
    }
    set label(value: FromToLabel) {
        this._label = value;
        this._placeholder = this.translateService.instant(value.label ?? '');
        this._placeholderStartDate = this.translateService.instant(value.from ?? '');
        this._placeholderEndDate = this.translateService.instant(value.to ?? '');
        this.stateChanges.next();
    }

    private _label: FromToLabel = {
        from: '',
        to: '',
        label: '',
    };

    get empty() {
        const {
            value: { from, to },
        } = this.form;

        return !from && !to;
    }

    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.form.disable() : this.form.enable();
        this.stateChanges.next();
    }
    private _disabled = false;

    @Input()
    get id(): string {
        return this._id;
    }
    set id(value: string) {
        this._id = value || this._uid;
    }
    private _id: string = '';

    get errorState(): boolean {
        return this.form.invalid && this.touched;
    }

    get shouldLabelFloat(): boolean {
        return this.focused || !this.empty;
    }

    @Input()
    autofocus: boolean = false;

    @Input()
    showHint: boolean = false;

    @Input()
    predefiniteIntervals: IDataRangeDuration[] = [
        { label: 'today', key: 'today', interval: 0 },
        { label: 'yesterday', key: 'yesterday', interval: 0 },
        { key: 'weeks', interval: 1 },
        { key: 'weeks', interval: 2 },
        { key: 'months', interval: 1 },
        { key: 'months', interval: 6 },
        { key: 'years', interval: 1 },
    ];

    private destroyed$ = new Subject<void>();
    constructor(
        private translateService: TranslateService,
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef<HTMLElement>,
        @Optional() @Self() public ngControl: NgControl,
        @Optional() @Inject(MAT_FORM_FIELD) public formField?: MatFormField,
    ) {
        if (this.ngControl !== null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
            if (this.form.valid) {
                this.onChange(this.value);
                this.dataRangeChange.emit(this.value);
            }
        });
    }

    setDescribedByIds(ids: string[]) {
        this.wrapperElement()?.nativeElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick(_event: MouseEvent): null {
        return null;
    }

    writeValue(value: IDataRangeInput | null): void {
        if (value === null) this.value = null;
        else {
            const { from: _from, to: _to } = value;
            let from: moment.Moment | null;
            if (!_from) from = null;
            else if (moment.isMoment(_from)) from = _from;
            else from = moment(_from);
            let to: moment.Moment | null;
            if (!_to) to = null;
            else if (moment.isMoment(_to)) to = _to;
            else to = moment(_to);
            this.value = { from, to };
        }
    }

    registerOnChange(fn: (value: IDataRange | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    getDateFormat = () => SE_UI_DATE_FORMATS.display.dateInput;

    hasStartDateError = (): boolean => this.form.controls.from.hasError('matStartDateInvalid');
    hasEndDateError = (): boolean => this.form.controls.to.hasError('matEndDateInvalid');

    onFocusIn(_event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    ngOnChanges() {
        this.stateChanges.next();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.stateChanges.complete();
        this.focusMonitor.stopMonitoring(this.elementRef);
    }

    reset = (event: MouseEvent) => {
        event.stopImmediatePropagation();
        this.value = null;
    };

    getLabelForInterval = (duration: IDataRangeDuration): string => {
        if (duration.label) return this.translateService.instant(duration.label);
        const key = duration.interval === 1 ? duration.key.substring(0, duration.key.length - 1) : duration.key;
        return `${duration.interval} ${this.translateService.instant(capitalizeFirstLetter(key))}`;
    };

    setPredefiniteInterval = (duration: IDataRangeDuration): void => {
        const { key: durationKey, interval: durationInterval } = duration;
        let from: moment.Moment | null = null;
        let to: moment.Moment | null = null;
        if (durationKey === 'today') {
            from = moment().utc().startOf('day');
            to = moment().utc().endOf('day');
        } else if (durationKey === 'yesterday') {
            from = moment().subtract(1, 'days').utc().startOf('day');
            to = moment().subtract(1, 'days').utc().endOf('day');
        } else {
            to = moment().utc().endOf('day');
            const interval: number = durationKey === 'weeks' ? durationInterval * 7 : durationInterval;
            const key: moment.unitOfTime.DurationConstructor = durationKey === 'weeks' ? 'days' : durationKey;
            from = moment(to).subtract(interval, key);
        }
        this.value = {
            from,
            to,
        };
        this.onEnter();
    };

    onEnter() {
        this.searchRequired.emit();
    }
}
